import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Plus, Trash2, Users, Shield, MapPin, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EPCI_OPTIONS = [
  'EPTB Vistre Vistrenque',
  "SYMBO (Bassin de l'Or)",
  'EPTB Lez',
  'EPTB Somme',
  'SDEA Bas-Rhin',
  'Alsace Centrale',
  'Seine-Maritime',
  'Hautes-Pyrénées'
];

const UserManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  // New user form
  const [newUser, setNewUser] = useState({
    email: '', password: '', name: '', role: 'viewer',
    territory_type: 'none', territory_value: ''
  });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newUser,
        territory_type: newUser.territory_type === 'none' ? null : newUser.territory_type,
        territory_value: newUser.territory_type === 'none' ? null : newUser.territory_value
      };
      await axios.post(`${API}/users`, payload, { withCredentials: true });
      toast.success('Utilisateur créé avec succès');
      setShowCreate(false);
      setNewUser({ email: '', password: '', name: '', role: 'viewer', territory_type: 'none', territory_value: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Supprimer ${email} ?`)) return;
    try {
      await axios.delete(`${API}/users/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') return <Badge className="bg-[#D4AF37] text-[#0F203C]"><Shield className="w-3 h-3 mr-1" />Admin</Badge>;
    return <Badge variant="outline" className="text-[#475569]"><Eye className="w-3 h-3 mr-1" />Viewer</Badge>;
  };

  const getTerritoryBadge = (user) => {
    if (!user.territory_type) return <Badge variant="secondary" className="bg-green-100 text-green-800">Tous les territoires</Badge>;
    if (user.territory_type === 'epci') return <Badge variant="secondary" className="bg-blue-100 text-blue-800"><MapPin className="w-3 h-3 mr-1" />EPCI: {user.territory_value}</Badge>;
    if (user.territory_type === 'commune') return <Badge variant="secondary" className="bg-purple-100 text-purple-800"><MapPin className="w-3 h-3 mr-1" />Commune: {user.territory_value}</Badge>;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F203C]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-[#0F203C] text-white py-6 px-6 mb-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="text-white hover:bg-white/10">
              <ArrowLeft className="w-5 h-5 mr-2" />Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
                <Users className="w-8 h-8" />Gestion des Utilisateurs
              </h1>
              <p className="text-sm opacity-90 mt-1">{users.length} utilisateur(s) enregistré(s)</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#D4AF37] hover:bg-[#b8962e] text-[#0F203C] font-semibold">
            <Plus className="w-4 h-4 mr-2" />Nouveau
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6">
        {/* Create User Form */}
        {showCreate && (
          <Card className="border-[#D4AF37] shadow-lg mb-6">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#0F203C]">Créer un Utilisateur</CardTitle>
              <CardDescription>Définissez les accès et la restriction territoriale</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-semibold text-[#0F203C]">Nom</Label>
                    <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} placeholder="Gestionnaire Nîmes" required className="mt-1" />
                  </div>
                  <div>
                    <Label className="font-semibold text-[#0F203C]">Email</Label>
                    <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="user@mayane.eu" required className="mt-1" />
                  </div>
                  <div>
                    <Label className="font-semibold text-[#0F203C]">Mot de passe</Label>
                    <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Min. 8 caractères" required className="mt-1" />
                  </div>
                  <div>
                    <Label className="font-semibold text-[#0F203C]">Rôle</Label>
                    <Select value={newUser.role} onValueChange={(val) => setNewUser({...newUser, role: val})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin (tous les territoires)</SelectItem>
                        <SelectItem value="viewer">Viewer (territoire restreint)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {newUser.role === 'viewer' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#F8FAFC] p-4 rounded-md border border-[#E2E8F0]">
                    <div>
                      <Label className="font-semibold text-[#0F203C]">Type de Territoire</Label>
                      <Select value={newUser.territory_type} onValueChange={(val) => setNewUser({...newUser, territory_type: val, territory_value: ''})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Tous les territoires</SelectItem>
                          <SelectItem value="epci">EPCI porteur de PAPI</SelectItem>
                          <SelectItem value="commune">Commune (Code Postal)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newUser.territory_type === 'epci' && (
                      <div>
                        <Label className="font-semibold text-[#0F203C]">EPCI</Label>
                        <Select value={newUser.territory_value} onValueChange={(val) => setNewUser({...newUser, territory_value: val})}>
                          <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner un EPCI" /></SelectTrigger>
                          <SelectContent>
                            {EPCI_OPTIONS.map(epci => (
                              <SelectItem key={epci} value={epci}>{epci}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {newUser.territory_type === 'commune' && (
                      <div>
                        <Label className="font-semibold text-[#0F203C]">Code Postal</Label>
                        <Input value={newUser.territory_value} onChange={(e) => setNewUser({...newUser, territory_value: e.target.value})} placeholder="30000" className="mt-1" />
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
                  <Button type="submit" className="bg-[#0F203C] hover:bg-[#0a1526] text-white">Créer l'utilisateur</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Users List */}
        <div className="space-y-4">
          {users.map((user) => (
            <Card key={user.email} className="border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#0F203C] flex items-center justify-center text-white font-bold text-lg">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F203C] text-lg">{user.name}</h3>
                      <p className="text-sm text-[#475569]">{user.email}</p>
                      <div className="flex gap-2 mt-2">
                        {getRoleBadge(user.role)}
                        {getTerritoryBadge(user)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {user.email !== currentUser?.email && (
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteUser(user.email)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info about territorial restrictions */}
        <Card className="border-[#D4AF37] bg-[#D4AF37]/5 mt-8 mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-[#0F203C]">Comment fonctionnent les restrictions territoriales ?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-[#475569]">
            <p><strong className="text-[#0F203C]">Admin</strong> : Accès complet à toutes les données, tous les territoires, et gestion des utilisateurs.</p>
            <p><strong className="text-[#0F203C]">Viewer EPCI</strong> : Ne voit que les données des communes appartenant à son EPCI. Dashboard, carte et export filtrés automatiquement.</p>
            <p><strong className="text-[#0F203C]">Viewer Commune</strong> : Ne voit que les données de sa commune (code postal). Dashboard, carte et export filtrés automatiquement.</p>
            <p className="italic mt-4">Exemple : Un gestionnaire EPTB Vistre ne verra que les 218 réponses de la zone Nîmes/Gard, pas celles de Montpellier ou de l'Alsace.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserManagement;
